import React, { useEffect, useRef } from 'react';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';

import './jquery';

import '@edtr-io/mathquill/build/mathquill.css';
import '@edtr-io/mathquill/build/mathquill.js';
import katex from 'katex';
import 'katex/dist/katex.css';
import mathquill4quill from 'mathquill4quill';
import 'mathquill4quill/mathquill4quill.css';

import { markdownToDelta } from 'pages/Teachers/shared/AssignmentDialog/parser';

import styles from './styles.module.scss';

window.katex = katex;

const operators = [
  ['\\pm', '\\pm'],
  ['\\sqrt{x}', '\\sqrt'],
  ['\\sqrt[3]{x}', '\\sqrt[3]{}'],
  ['\\frac{x}{y}', '\\frac'],
  ['\\sum^{s}_{x}{d}', '\\sum'],
  ['\\prod^{s}_{x}{d}', '\\prod'],
  ['\\coprod^{s}_{x}{d}', '\\coprod'],
  ['\\int^{s}_{x}{d}', '\\int'],
  ['\\binom{n}{k}', '\\binom'],
  ['\\div', '\\div'],
  ['e^x', 'e^{x}'],
  ['x^y', 'x^{y}'],
  ['\\sin(x)', '\\sin'],
  ['\\cos(x)', '\\cos'],
  ['\\tan(x)', '\\tan'],
  ['\\cot(x)', '\\cot'],
  ['\\sec(x)', '\\sec'],
  ['\\csc(x)', '\\csc']
];

const LATEX_EDITOR_ENABLED =
  process.env.REACT_APP_LATEX_EDITOR_ENABLED === 'true';

const QuestionField = ({ initialValue, onChange, readOnlyMode }) => {
  const reactQuill = useRef(null);
  const cursorRef = useRef(null);

  useEffect(() => {
    if (!reactQuill.current) return;

    if (LATEX_EDITOR_ENABLED) return;

    const editor = reactQuill.current.getEditor();
    if (!editor) return;

    if (initialValue?.ops && Array.isArray(initialValue.ops)) {
      const range = editor.getSelection();
      editor.setContents(initialValue);
      cursorRef.current = range;
      return;
    }

    if (initialValue && typeof initialValue === 'string') {
      const contents = editor.getContents();
      const isEmpty =
        contents.ops.length === 1 && contents.ops[0].insert === '\n';

      if (readOnlyMode && !isEmpty) return;

      markdownToDelta({ markdownData: initialValue, editor });
      onChange(editor.getContents());
    }
  }, [initialValue, onChange, readOnlyMode]);

  useEffect(() => {
    if (!reactQuill.current) return;

    const enableMathQuillFormulaAuthoring = mathquill4quill({ Quill, katex });
    // Enable math formula UI
    if (!readOnlyMode) {
      enableMathQuillFormulaAuthoring(reactQuill.current.editor, { operators });
    }
  }, [reactQuill, readOnlyMode]);

  useEffect(() => {
    const editor = reactQuill.current?.getEditor();

    if (!editor) return;
    if (!cursorRef) return;
    if (!cursorRef.current) return;

    const { index, length } = cursorRef.current;
    const contentLength = editor.getLength();

    const safeIndex = Math.min(index, contentLength - 1);

    editor.setSelection(safeIndex, length || 0);
  }, [initialValue]);

  const editorConfiguration = (() => {
    const latexEnabled = !!LATEX_EDITOR_ENABLED;

    return {
      isFormulaEnabled: !latexEnabled,
      markupFormula: latexEnabled ? null : 'formula',
      placeholder: latexEnabled
        ? 'Type text here.'
        : 'Type text here, or click on the formula button to enter math.'
    };
  })();

  return (
    <>
      {' '}
      {readOnlyMode ? (
        <ReactQuill
          ref={reactQuill}
          readOnly={true}
          theme="bubble" // Optional: "bubble" theme looks better for read-only
        />
      ) : (
        <ReactQuill
          ref={reactQuill}
          className={styles.editor}
          id="editor"
          onChange={(value, delta, source, editor) => {
            const contents = editor.getContents();

            onChange(contents);
          }}
          modules={{
            formula: editorConfiguration.isFormulaEnabled,
            toolbar: [
              [{ header: [1, 2, 3, false] }],
              [{ list: 'ordered' }, { list: 'bullet' }],
              [
                'bold',
                'italic',
                'underline',
                editorConfiguration.markupFormula
              ],
              ['clean']
            ]
          }}
          placeholder={editorConfiguration.placeholder}
          theme="snow"
        />
      )}
    </>
  );
};

export default QuestionField;
